import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Card, Chip, Sheet, Table, Typography } from '@mui/joy';
import { Info, RestartAlt } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_ADMIN_API_BASE || 'http://localhost:9300';

const TYPE_LABELS = { restart_services: 'Restart hub', reboot: 'Reboot' };
const STATUS_COLORS = {
    pending: 'neutral',
    running: 'primary',
    succeeded: 'success',
    failed: 'danger',
    expired: 'warning',
};

const requesterLabel = (item) => {
    const who = item.created_by_name || item.created_by_email;
    const id = item.created_by != null ? ` #${item.created_by}` : '';
    if (item.created_by_type === 'admin') return who ? `Admin ${who}` : `Admin${id}`;
    if (item.created_by_type === 'client') return who ? `User ${who}` : `User${id}`;
    return '—';
};

const formatDuration = (item) => {
    if (!item.completed_at) return '—';
    const seconds = Math.round((item.completed_at - (item.started_at || item.created_at)) / 1000);
    return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

/** Restart/reboot requests for a hub, newest first. Reloads when refreshKey changes. */
const HubCommandHistory = ({ hubId, refreshKey }) => {
    const { session } = useAuth();
    const [items, setItems] = useState([]);
    const [error, setError] = useState(null);
    const sessionRef = useRef(session);
    sessionRef.current = session;

    const load = useCallback(async () => {
        if (!sessionRef.current?.accessToken) return;
        try {
            const res = await fetch(`${API_BASE}/device/${hubId}/commands?limit=20`, {
                headers: {
                    Authorization: sessionRef.current.accessToken,
                    'Vendor-Id': sessionRef.current.vendorId,
                },
            });
            const body = await res.json().catch(() => null);
            if (!res.ok) {
                setError(body?.error || `Failed to load restart history (${res.status}).`);
                return;
            }
            setError(null);
            setItems(body?.items || []);
        } catch (e) {
            setError('Unable to reach the admin API.');
        }
    }, [hubId]);

    useEffect(() => {
        load();
    }, [load, refreshKey, session?.accessToken]);

    return (
        <Box sx={{ mb: 4 }}>
            <Typography level="h3" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <RestartAlt sx={{ mr: 1 }} />
                Restart History
            </Typography>
            {error && (
                <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {items.length === 0 ? (
                <Card variant="soft" sx={{ p: 3, textAlign: 'center' }}>
                    <Info sx={{ fontSize: 48, color: 'neutral.400', mb: 2 }} />
                    <Typography level="body-sm" textColor="neutral.500">
                        No restarts requested yet
                    </Typography>
                </Card>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
                    <Box sx={{ overflow: 'auto' }}>
                        <Table hoverRow size="sm" sx={{ minWidth: 760 }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 180 }}>Requested</th>
                                    <th style={{ width: 120 }}>Action</th>
                                    <th>Requested by</th>
                                    <th style={{ width: 110 }}>Status</th>
                                    <th style={{ width: 90 }}>Took</th>
                                    <th>Error</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => (
                                    <tr key={item.id}>
                                        <td>
                                            <Typography level="body-sm">
                                                {new Date(item.created_at).toLocaleString()}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-sm">{TYPE_LABELS[item.type] || item.type}</Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-sm">{requesterLabel(item)}</Typography>
                                        </td>
                                        <td>
                                            <Chip size="sm" variant="soft" color={STATUS_COLORS[item.status] || 'neutral'}>
                                                {item.status}
                                            </Chip>
                                        </td>
                                        <td>
                                            <Typography level="body-sm">{formatDuration(item)}</Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-sm" color={item.error ? 'danger' : undefined}>
                                                {item.error || '—'}
                                            </Typography>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Box>
                </Sheet>
            )}
        </Box>
    );
};

export default HubCommandHistory;
